import { useEffect, useState } from 'react'
import { client } from '../lib/sanity'
import { AnimatePresence, motion } from 'framer-motion'
import { CheckCircle, Copy } from 'lucide-react'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import ContactForm from '../components/contact/ContactForm'
import LinksDirectory from '../components/contact/LinksDirectory'

const CONTACT_QUERY = `*[_type == "contact"][0]{
    title,
    subtitle,
    email,
    phone,
    availability,
    socialLinks[]{
        _key,
        name,
        link,
        logo,
        description,
        category,
        tags,
        _createdAt
    }
}`

const DEFAULT_SUBTITLE = "Open to research collaborations, consulting, speaking, and open-source discussions."
const RESPONSE_TIME = "Typically replies within 24-48 hours."

export default function Contact() {
    const [data, setData] = useState({
        title: 'Contact Me',
        subtitle: '',
        email: '',
        phone: '',
        socialLinks: [],
    })
    const [loading, setLoading] = useState(true)
    const [toastMessage, setToastMessage] = useState('')

    useEffect(() => {
        let mounted = true
        client
            .fetch(CONTACT_QUERY)
            .then((res) => {
                if (!mounted) return
                const links = Array.isArray(res?.socialLinks) ? res.socialLinks : []
                setData({
                    title: res?.title || 'Contact Me',
                    subtitle: res?.subtitle || '',
                    email: res?.email || '',
                    phone: res?.phone || '',
                    availability: res?.availability || '',
                    socialLinks: links,
                })
                setLoading(false)
            })
            .catch(() => setLoading(false))
        return () => {
            mounted = false
        }
    }, [])

    const showToast = (msg) => {
        setToastMessage(msg)
        setTimeout(() => setToastMessage(''), 5000)
    }

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text)
        showToast('Copied to clipboard!')
    }

    return (
        <div className="min-h-screen bg-white dark:bg-zinc-950">
            <AnimatePresence>
                {toastMessage && (
                    <Card
                        as={motion.div}
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="fixed top-4 right-4 z-50 text-black dark:text-white px-4 py-3 shadow-brutal flex items-center gap-2"
                    >
                        <CheckCircle className="w-5 h-5 text-accent" />
                        {toastMessage}
                    </Card>
                )}
            </AnimatePresence>

            <section className="max-w-6xl mx-auto py-12 md:py-24 px-6">
                <motion.div className="mb-16 max-w-2xl" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <h2 className="text-5xl md:text-6xl font-serif font-bold text-black dark:text-white">{data.title || 'Contact Me'}</h2>
                    <p className="mt-4 text-lg text-zinc-700 dark:text-zinc-300">{data.subtitle || DEFAULT_SUBTITLE}</p>
                    {data.availability && <p className="mt-2 text-sm text-accent font-mono font-medium">{data.availability}</p>}
                </motion.div>

                {(data.email || data.phone) && (
                    <Card as={motion.div} className="mb-8 p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.1 }}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {data.email && (
                                <div>
                                    <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-2">Email</p>
                                    <div className="flex items-center gap-2">
                                        <a href={`mailto:${data.email}`} className="text-accent font-semibold hover:underline">
                                            {data.email}
                                        </a>
                                        <Button variant="ghost" onClick={() => copyToClipboard(data.email)} title="Copy email" className="p-1.5" aria-label="Copy email">
                                            <Copy className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">{RESPONSE_TIME}</p>
                                </div>
                            )}
                            {data.phone && (
                                <div>
                                    <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-2">Phone</p>
                                    <a href={`tel:${data.phone}`} className="text-accent font-semibold hover:underline">
                                        {data.phone}
                                    </a>
                                </div>
                            )}
                        </div>
                    </Card>
                )}

                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-start">
                    <motion.div className="order-2 md:order-1 md:col-span-5" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
                        <ContactForm onSent={showToast} />
                    </motion.div>

                    <motion.div className="order-1 md:order-2 md:col-span-7" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
                        <div className="md:sticky md:top-20">
                            <LinksDirectory links={data.socialLinks} loading={loading} />
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    )
}
